<script setup>
// == Unichat ==
// Chat do Setor: canal ÚNICO e permanente por Setor (Team). Fica fixo na tela,
// independente da conversa de cliente aberta. Compartilhado pelos membros do time.
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { emitter } from 'shared/helpers/mitt';
import {
  getMySectorChats,
  getSectorChatMessages,
  createSectorChatMessage,
} from 'dashboard/unichat/api/unichat';

const { accountId } = useAccount();
const currentChat = useMapGetter('getSelectedChat');
const currentUser = useMapGetter('getCurrentUser');

const myTeams = ref([]);
const selectedTeamId = ref(null);
const messages = ref([]);
const content = ref('');
const loadingTeams = ref(true);
const loading = ref(false);
const sending = ref(false);
const threadRef = ref(null);

const hasTeams = computed(() => myTeams.value.length > 0);
const selectedTeam = computed(
  () => myTeams.value.find(t => t.id === selectedTeamId.value) || null
);

const scrollToBottom = () => {
  nextTick(() => {
    const el = threadRef.value;
    if (el) el.scrollTop = el.scrollHeight;
  });
};

const addMessage = msg => {
  if (!msg) return;
  if (messages.value.some(m => m.id === msg.id)) return;
  messages.value.push(msg);
  scrollToBottom();
};

const loadMessages = async () => {
  messages.value = [];
  if (!selectedTeamId.value) return;

  loading.value = true;
  try {
    const { data } = await getSectorChatMessages(
      accountId.value,
      selectedTeamId.value
    );
    messages.value = data?.payload ?? [];
    scrollToBottom();
  } catch (e) {
    messages.value = [];
  } finally {
    loading.value = false;
  }
};

const loadTeams = async () => {
  loadingTeams.value = true;
  try {
    const { data } = await getMySectorChats(accountId.value);
    myTeams.value = data?.payload ?? [];

    // Default: o setor da conversa aberta (se eu for membro), senão o primeiro.
    const chatTeamId = currentChat.value?.meta?.team?.id;
    const inMyTeams = myTeams.value.some(t => t.id === chatTeamId);
    selectedTeamId.value = inMyTeams
      ? chatTeamId
      : myTeams.value[0]?.id || null;
  } catch (e) {
    myTeams.value = [];
  } finally {
    loadingTeams.value = false;
  }
};

const sendMessage = async () => {
  const text = content.value.trim();
  if (!text || sending.value || !selectedTeamId.value) return;

  sending.value = true;
  try {
    const { data } = await createSectorChatMessage(
      accountId.value,
      selectedTeamId.value,
      text
    );
    content.value = '';
    addMessage(data);
  } catch (e) {
    // mantém o texto digitado em caso de erro
  } finally {
    sending.value = false;
  }
};

const onRealtimeMessage = data => {
  if (!data) return;
  if (Number(data.team_id) !== Number(selectedTeamId.value)) return;
  addMessage(data);
};

const isOwn = msg => Number(msg?.sender?.id) === Number(currentUser.value?.id);

const initials = name =>
  (name || '?')
    .split(' ')
    .map(n => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

const formatTime = ts => {
  if (!ts) return '';
  const d = new Date(ts * 1000);
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

// Ao abrir uma conversa de um setor que eu participo, troca para esse canal.
watch(
  () => currentChat.value?.meta?.team?.id,
  teamId => {
    if (teamId && myTeams.value.some(t => t.id === teamId)) {
      selectedTeamId.value = teamId;
    }
  }
);

watch(selectedTeamId, () => loadMessages());

onMounted(async () => {
  await loadTeams();
  emitter.on('UNICHAT_SECTOR_MESSAGE_CREATED', onRealtimeMessage);
});

onBeforeUnmount(() => {
  emitter.off('UNICHAT_SECTOR_MESSAGE_CREATED', onRealtimeMessage);
});
</script>

<template>
  <div
    v-if="loadingTeams || hasTeams"
    class="unichat-sector-chat flex flex-col flex-shrink-0 w-[300px] h-full border-l border-n-weak bg-n-background relative overflow-hidden"
  >
    <!-- Header -->
    <div class="flex items-center gap-2.5 px-4 py-3 border-b border-n-weak">
      <div
        class="flex items-center justify-center rounded-lg size-9 shrink-0"
        style="background: rgba(29, 158, 117, 0.16); color: #1d9e75"
      >
        <i class="i-lucide-users-round size-5" />
      </div>
      <div class="min-w-0 flex-1">
        <p class="text-sm font-semibold leading-tight text-n-slate-12 truncate">
          Chat do Setor
        </p>
        <p class="text-xs text-n-slate-11 truncate">
          {{ selectedTeam?.name || 'Setor' }} · interno
        </p>
      </div>
      <!-- Seletor de setor (quando o agente está em mais de um) -->
      <select
        v-if="myTeams.length > 1"
        v-model="selectedTeamId"
        class="max-w-[110px] text-xs rounded-md px-2 py-1 bg-n-alpha-1 text-n-slate-12 border border-n-weak outline-none"
      >
        <option v-for="t in myTeams" :key="t.id" :value="t.id">
          {{ t.name }}
        </option>
      </select>
    </div>

    <!-- Aviso -->
    <div
      class="flex items-center justify-center gap-1.5 px-4 py-2 text-xs text-center border-b text-n-slate-11 border-n-weak"
    >
      <i class="i-lucide-lock size-3" style="color: #1d9e75" />
      Conversa interna — o cidadão não vê.
    </div>

    <!-- Sem setor -->
    <div
      v-if="!loadingTeams && !hasTeams"
      class="flex flex-col items-center justify-center flex-1 gap-2 px-6 text-center"
    >
      <i class="i-lucide-users size-7 text-n-slate-9" />
      <p class="text-sm text-n-slate-11">
        Você não faz parte de nenhum setor.
      </p>
    </div>

    <!-- Thread -->
    <div
      v-else
      ref="threadRef"
      class="flex flex-col flex-1 gap-3 p-4 overflow-y-auto"
    >
      <div
        v-if="loading || loadingTeams"
        class="flex items-center justify-center flex-1 text-xs text-n-slate-10"
      >
        Carregando…
      </div>
      <div
        v-else-if="!messages.length"
        class="flex flex-col items-center justify-center flex-1 gap-2 text-center"
      >
        <i class="i-lucide-messages-square size-7 text-n-slate-9" />
        <p class="text-xs text-n-slate-10">
          Nenhuma mensagem ainda. Combine o atendimento com seu setor por aqui.
        </p>
      </div>

      <template v-else>
        <div
          v-for="msg in messages"
          :key="msg.id"
          class="flex items-start gap-2"
          :class="isOwn(msg) ? 'flex-row-reverse' : ''"
        >
          <div
            class="flex items-center justify-center text-xs font-semibold rounded-full size-6 shrink-0"
            :style="
              isOwn(msg)
                ? 'background:rgba(31,147,255,0.2);color:#1f93ff'
                : 'background:rgba(29,158,117,0.2);color:#1d9e75'
            "
          >
            {{ initials(msg.sender?.name) }}
          </div>
          <div
            class="px-3 py-2 max-w-[80%]"
            :class="
              isOwn(msg)
                ? 'rounded-xl rounded-tr-sm bg-n-solid-blue'
                : 'rounded-xl rounded-tl-sm bg-n-slate-3'
            "
          >
            <p
              v-if="!isOwn(msg)"
              class="text-xs font-semibold mb-0.5"
              style="color: #1d9e75"
            >
              {{ msg.sender?.name }}
            </p>
            <p
              class="text-sm leading-snug"
              :class="isOwn(msg) ? 'text-white' : 'text-n-slate-12'"
            >
              {{ msg.content }}
            </p>
            <p
              class="mt-1 text-[10px] text-right"
              :class="isOwn(msg) ? 'text-white/70' : 'text-n-slate-10'"
            >
              {{ formatTime(msg.created_at) }}
            </p>
          </div>
        </div>
      </template>
    </div>

    <!-- Composer -->
    <div
      v-if="hasTeams"
      class="flex items-center gap-2 px-3 py-3 border-t border-n-weak"
    >
      <input
        v-model="content"
        type="text"
        placeholder="Mensagem interna do setor…"
        class="flex-1 px-3 py-2 text-sm rounded-full outline-none bg-n-alpha-1 text-n-slate-12 border border-n-weak placeholder:text-n-slate-10"
        :disabled="sending"
        @keyup.enter="sendMessage"
      />
      <button
        class="flex items-center justify-center text-white rounded-full size-9 shrink-0 disabled:opacity-50"
        style="background: #1d9e75"
        :disabled="sending || !content.trim()"
        @click="sendMessage"
      >
        <i class="i-lucide-send size-4" />
      </button>
    </div>
  </div>
</template>
